import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Alert } from 'reactstrap';

import { messages } from '../../i18n/messages';

import './Alert.scss';

const propTypes = {

	/**
	 * The text to display in the alert.
	 */
	text: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.element,
		PropTypes.array,
		PropTypes.shape({}),
	]),

	/**
	 * Flag prefix
	 *
	 * By default the prefix text will be shown, but it can be hidden by passing false.
	 */
	prefix: PropTypes.bool,

	/**
	 * The text or DOM to display in the alert.
	 */
	children: PropTypes.node,

	/**
	 * Flag dismissible
	 *
	 * By default the alert will be dismissible, but it can be fixed by passing false.
	 */
	dismissible: PropTypes.bool,

	/**
	 * Callback to handle additional logic while dismissing the alert.
	 */
	onDismiss: PropTypes.func,

};

const defaultProps = {
	prefix: true,
	dismissible: true,
};

const TCAlert = (alertProps) => {

	const {
		color,
		dismissible,
		children,
		onDismiss,
		...restProps
	} = alertProps;

	const [isOpen, setIsOpen] = useState(true);

	const handleDismiss = () => {

		setIsOpen(false);

		if (onDismiss) {
			onDismiss();
		}

	};

	return (
		<div className="tc-alert">
			<Alert
				{...restProps}
				color={color}
				isOpen={isOpen}
				toggle={dismissible ? handleDismiss : null}>
				{children}
			</Alert>
		</div>
	);

};

/**
 * Renders a simple info alert.
 *
 * @param   {object} props  The props.
 * @returns {string}        The rendered component.
 */
export const InfoAlert = (props) => {

	const {
		text,
		prefix,
		dismissible,
		children,
		onDismiss,
		...restProps
	} = props;

	const content = text || children;

	return (
		<TCAlert
			{...restProps}
			color="info"
			dismissible={dismissible}
			onDismiss={onDismiss}>
			{prefix &&
				<strong>
					{' '}
					<FormattedMessage {...messages.TC_COMPONENTS_ALERT_INFO_PREFIX} />
					{' '}
				</strong>
			}
			{content}
		</TCAlert>
	);
};

InfoAlert.propTypes = propTypes;
InfoAlert.defaultProps = defaultProps;

/**
 * Renders a simple error alert.
 *
 * @param   {object} props  The props.
 * @returns {string}        The rendered component.
 */
export const DangerAlert = (props) => {

	const {
		text,
		prefix,
		dismissible,
		children,
		onDismiss,
		...restProps
	} = props;

	const content = text || children;

	return (
		<TCAlert
			{...restProps}
			color="danger"
			dismissible={dismissible}
			onDismiss={onDismiss}>
			<i
				aria-hidden="true"
				className="fas fa-exclamation-triangle" />
			{prefix &&
				<strong>
					{' '}
					<FormattedMessage {...messages.TC_COMPONENTS_ALERT_ERROR_PREFIX} />
					{' '}
				</strong>
			}
			{content}
		</TCAlert>
	);
};

DangerAlert.propTypes = propTypes;
DangerAlert.defaultProps = defaultProps;

/**
 * Renders a simple success alert.
 *
 * @param   {object} props  The props.
 * @returns {string}        The rendered component.
 */
export const SuccessAlert = (props) => {

	const {
		text,
		prefix,
		dismissible,
		children,
		onDismiss,
		...restProps
	} = props;

	const content = text || children;

	return (
		<TCAlert
			{...restProps}
			color="success"
			dismissible={dismissible}
			onDismiss={onDismiss}>
			<i
				aria-hidden="true"
				className="fas fa-check-circle" />
			{prefix &&
				<strong>
					{' '}
					<FormattedMessage {...messages.TC_COMPONENTS_ALERT_SUCCESS_PREFIX} />
					{' '}
				</strong>
			}
			{content}
		</TCAlert>
	);

};

SuccessAlert.propTypes = propTypes;
SuccessAlert.defaultProps = defaultProps;

/**
 * Renders a simple warning alert.
 *
 * @param   {object} props  The props.
 * @returns {string}        The rendered component.
 */
export const WarningAlert = (props) => {

	const {
		text,
		prefix,
		dismissible,
		children,
		onDismiss,
		...restProps
	} = props;

	const content = text || children;

	return (
		<TCAlert
			{...restProps}
			color="warning"
			dismissible={dismissible}
			onDismiss={onDismiss}>
			<i
				aria-hidden="true"
				className="fas fa-exclamation-triangle" />
			{prefix &&
				<strong>
					{' '}
					<FormattedMessage {...messages.TC_COMPONENTS_ALERT_WARNING_PREFIX} />
					{' '}
				</strong>
			}
			{content}
		</TCAlert>
	);
};

WarningAlert.propTypes = propTypes;
WarningAlert.defaultProps = defaultProps;

const alert = {
	SuccessAlert,
	DangerAlert,
	InfoAlert,
	WarningAlert,
};

export default alert;
